import { Component, OnInit, OnDestroy, signal, inject, ChangeDetectionStrategy } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { PwaService } from '../../services/pwa.service';

@Component({
  selector: 'jk-pwa-install-prompt',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatSnackBarModule
],
  template: `
    @if (showPrompt() && !isPwaMode()) {
      <div class="install-prompt-container">
        <mat-card class="install-prompt-card">
          <mat-card-content>
            <div class="prompt-content">
              <mat-icon class="app-icon">get_app</mat-icon>
              <div class="prompt-text">
                <h3>安裝應用程式</h3>
                <p>將此應用程式安裝到您的裝置，享受更好的使用體驗：</p>
                <ul>
                  <li>離線使用</li>
                  <li>更快的載入速度</li>
                  <li>推播通知</li>
                  <li>全螢幕體驗</li>
                </ul>
              </div>
            </div>
          </mat-card-content>

          <mat-card-actions align="end">
            <button mat-button (click)="dismissPrompt()">
              稍後再說
            </button>
            <button mat-raised-button color="primary" (click)="installApp()">
              <mat-icon>get_app</mat-icon>
              立即安裝
            </button>
          </mat-card-actions>
        </mat-card>
      </div>
    }

    <!-- 浮動安裝按鈕 -->
    @if (showFloatingButton() && !isPwaMode()) {
      <button
        mat-fab
        color="primary"
        class="floating-install-button"
        (click)="installApp()"
        matTooltip="安裝應用程式">
        <mat-icon>get_app</mat-icon>
      </button>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    .install-prompt-container {
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1000;
      max-width: 400px;
      width: calc(100% - 40px);
    }

    .install-prompt-card {
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
      border-radius: 12px;
    }

    .prompt-content {
      display: flex;
      align-items: flex-start;
      gap: 16px;
    }

    .app-icon {
      font-size: 48px;
      width: 48px;
      height: 48px;
      color: #1976d2;
      flex-shrink: 0;
    }

    .prompt-text h3 {
      margin: 0 0 8px 0;
      font-size: 18px;
      font-weight: 500;
    }

    .prompt-text p {
      margin: 0 0 12px 0;
      color: #666;
    }

    .prompt-text ul {
      margin: 0;
      padding-left: 20px;
      color: #666;
    }

    .prompt-text li {
      margin: 4px 0;
    }

    .floating-install-button {
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 1000;
    }

    @media (max-width: 600px) {
      .install-prompt-container {
        bottom: 10px;
        width: calc(100% - 20px);
      }

      .prompt-content {
        flex-direction: column;
        text-align: center;
      }

      .floating-install-button {
        bottom: 10px;
        right: 10px;
      }
    }

    /* 動畫效果 */
    .install-prompt-container {
      animation: slideUp 0.3s ease-out;
    }

    .floating-install-button {
      animation: bounceIn 0.5s ease-out;
    }

    @keyframes slideUp {
      from {
        transform: translateX(-50%) translateY(100%);
        opacity: 0;
      }
      to {
        transform: translateX(-50%) translateY(0);
        opacity: 1;
      }
    }

    @keyframes bounceIn {
      0% {
        transform: scale(0);
        opacity: 0;
      }
      50% {
        transform: scale(1.1);
      }
      100% {
        transform: scale(1);
        opacity: 1;
      }
    }
  `]
})
export class PwaInstallPromptComponent implements OnInit, OnDestroy {
  private pwaService = inject(PwaService);

  private destroy$ = new Subject<void>();
  private promptDismissed = false;
  private showPromptDelay = 30000; // 30 seconds

  showPrompt = signal<boolean>(false);
  showFloatingButton = signal<boolean>(false);
  isPwaMode = signal<boolean>(false);

  ngOnInit(): void {
    this.isPwaMode.set(this.pwaService.isPwaMode());

    // 監聽安裝提示可用性
    this.pwaService.installPromptAvailable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(available => {
        if (available && !this.promptDismissed && !this.isPwaMode()) {
          // 延遲顯示提示，讓使用者先熟悉應用程式
          setTimeout(() => {
            if (!this.promptDismissed) {
              this.showPrompt.set(true);

              // 10 秒後隱藏卡片提示，顯示浮動按鈕
              setTimeout(() => {
                this.showPrompt.set(false);
                this.showFloatingButton.set(true);
              }, 10000);
            }
          }, this.showPromptDelay);
        }
      });

    // 檢查是否已經安裝
    if (this.isPwaMode()) {
      this.showPrompt.set(false);
      this.showFloatingButton.set(false);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async installApp(): Promise<void> {
    try {
      const installed = await this.pwaService.showInstallPrompt();
      if (installed) {
        this.showPrompt.set(false);
        this.showFloatingButton.set(false);
        this.promptDismissed = true;

        // 顯示安裝成功訊息
        this.showInstallSuccessMessage();
      }
    } catch (error) {
      console.error('Failed to install PWA:', error);
    }
  }

  dismissPrompt(): void {
    this.showPrompt.set(false);
    this.promptDismissed = true;

    // 稍後顯示浮動按鈕
    setTimeout(() => {
      if (!this.isPwaMode()) {
        this.showFloatingButton.set(true);
      }
    }, 60000); // 1 分鐘後顯示浮動按鈕
  }

  private showInstallSuccessMessage(): void {
    // 創建成功訊息元素
    const successMessage = document.createElement('div');
    successMessage.innerHTML = `
      <div style="
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: #4caf50;
        color: white;
        padding: 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        z-index: 10000;
        text-align: center;
        max-width: 300px;
      ">
        <div style="font-size: 48px; margin-bottom: 10px;">✓</div>
        <div style="font-size: 18px; font-weight: 500; margin-bottom: 5px;">安裝成功！</div>
        <div style="font-size: 14px;">應用程式已安裝到您的裝置</div>
      </div>
    `;

    document.body.appendChild(successMessage);

    // 3 秒後移除訊息
    setTimeout(() => {
      document.body.removeChild(successMessage);
    }, 3000);
  }
}
